import React, { useState } from 'react';
import axios from 'axios';

import './style/Questionnaire.css';

const entryQuestion = {
  id: "role",
  question: "Für welche Stelle interessierst Du dich?",
  options: [
    { label: "Mitarbeiter/in / Helfer/in in der Galvanik (m/w/d)", type: "house", icon: false },
    { label: "Mitarbeiter/in in Endkontrolle und Versand (m/w/d)", type: "house", icon: false },
  ],
};

const questionSets = {
  house: [],
};

const sharedFinalQuestions = [
  {
    id: "berufsausbildung",
    question: "Hast Du eine abgeschlossene Berufsausbildung?",
    options: [
      { label: "Ja", icon: false },
      { label: "Nein", icon: false },
    ],
  },
  {
    id: "berufserfahrung",
    question: "Wie viele Berufserfahrung hast Du?",
    options: [
      { label: "Keine", icon: false },
      { label: "< als 3 Jahre", icon: false },
      { label: "3-5 Jahre", icon: false },
      { label: "> 5 Jahre", icon: false },
    ],
  },
  {
    id: "nachricht",
    question: "Möchtest du uns noch was über Dich erzählen? (optional)",
    type: "textarea",
    icon: false,
  },
];

const contactFormId = "contactForm";

const Questionnaire = () => {
  const [selectedType, setSelectedType] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [inputValues, setInputValues] = useState({});
  const [contactData, setContactData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [contactErrors, setContactErrors] = useState({ name: "", email: "", phone: "" });
  const [sendDataSuccess, setSendDataSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const questions = React.useMemo(() => {
    if (selectedType) {
      const selectedQuestions = questionSets[selectedType] || [];
      return [entryQuestion, ...selectedQuestions, ...sharedFinalQuestions, { id: contactFormId, question: "Deine Kontaktdaten" }];
    }
    return [entryQuestion];
  }, [selectedType]);

  const currentQuestion = questions[currentStep];
  const totalQuestions = questions.length;
  const progressPercent = ((currentStep + 1) / totalQuestions) * 100;

  const handleAnswer = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));

    if (id === "role") {
      const selectedOption = entryQuestion.options.find(opt => opt.label === value);
      setSelectedType(selectedOption?.type);
      setCurrentStep(1);
      return;
    }

    if (id === contactFormId) return;

    setTimeout(() => {
      if (currentStep < questions.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    }, 300);
  };

  const handleInputChange = (id, val) => {
    setInputValues(prev => ({ ...prev, [id]: val }));
  };

  const handleInputSubmit = (id) => {
    handleAnswer(id, inputValues[id] || "");
  };

  const handleBack = () => {
    if (currentStep === 1) {
      setSelectedType(null);
      setCurrentStep(0);
      setAnswers({});
      setInputValues({});
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleContactChange = (field, value) => {
    setContactData(prev => ({ ...prev, [field]: value }));
  };

  const validateContactForm = () => {
    let valid = true;
    const errors = { name: "", email: "", phone: "" };

    if (!contactData.name.trim()) {
      errors.name = "Bitte Name eingeben";
      valid = false;
    }

    if (!contactData.email.trim()) {
      errors.email = "Bitte E-Mail eingeben";
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(contactData.email)) {
      errors.email = "Bitte gültige E-Mail eingeben";
      valid = false;
    }

    if (!contactData.phone.trim()) {
      errors.phone = "Bitte Telefonnummer eingeben";
      valid = false;
    }

    setContactErrors(errors);
    return valid;
  };

  const handleContactSubmit = async () => {
    if (!validateContactForm()) return;
    setIsLoading(true);

    const dataToSend = {
      answers,
      contact: contactData,
    };

    console.log("Sende Daten:", dataToSend);

    try {
      const response = await axios.post('/backend/index.php', dataToSend, {
        headers: { 'Content-Type': 'application/json' }
      });

      console.log("Server Antwort:", response.data);

      if (response.data.success) {
        setSendDataSuccess(true);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'rsmFormSubmission',
          rsmFormName: 'KarriereFormular',
          rsmEmail: contactData.email
        });
      } else {
        alert(response.data.error || "Fehler beim Senden.");
      }
    } catch (err) {
      console.error(err);
      console.log("Serverfehler:", err.response?.data);
      alert("Fehler beim Senden. Bitte versuchen Sie es später erneut.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="questionnaire-container">
      {!sendDataSuccess ? (
        <>
          <h2 className="questionnaire-heading">
            {currentStep === 0 ? <>Frage 1 von 4</> : <>Frage {currentStep + 1} von {totalQuestions}</>}
          </h2>

          <div className="progress-bar-background">
            <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }} />
          </div>

          <div className="question-container">
            <p className="question-text">{currentQuestion.question}</p>

            {currentQuestion.options && !currentQuestion.type && (
              <div className="options-grid">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={idx}
                    className={`option-button ${answers[currentQuestion.id] === option.label ? 'selected' : ''}`}
                    onClick={() => handleAnswer(currentQuestion.id, option.label)}
                  >
                    {option.icon && <div>{option.icon}</div>}
                    {option.label}
                  </button>
                ))}
              </div>
            )}

            {currentQuestion.type === "textarea" && (
              <div className="textarea-wrapper">
                <textarea
                  className="textarea-field"
                  rows={6}
                  placeholder="Erzähl uns mehr über Dich..."
                  value={inputValues[currentQuestion.id] || ''}
                  onChange={e => handleInputChange(currentQuestion.id, e.target.value)}
                />
                <button className="button-next" onClick={() => handleInputSubmit(currentQuestion.id)}>Weiter</button>
              </div>
            )}
          </div>

          {currentQuestion.id === contactFormId && (
            <div className="contact-form-wrapper">
              {["name", "email", "phone"].map((field) => (
                <div key={field} className="floating-label">
                  <input
                    type={field === "email" ? "email" : "text"}
                    placeholder=" "
                    value={contactData[field]}
                    onChange={(e) => handleContactChange(field, e.target.value)}
                    required
                    style={contactErrors[field] ? { border: '2px solid red' } : {}}
                  />
                  <label>
                    {field === "phone"
                      ? "Telefon*"
                      : field.charAt(0).toUpperCase() + field.slice(1) + "*"}
                  </label>
                  {contactErrors[field] && <p style={{ color: 'red' }}>{contactErrors[field]}</p>}
                </div>
              ))}

              <p style={{ fontSize: '12px' }}>
                Wir verarbeiten Ihre Daten zum Zwecke der Bearbeitung Ihrer Anfrage.
                <br />
                Bitte beachten Sie unsere Datenschutzerklärung.
              </p>

              <button className="button-submit" onClick={handleContactSubmit} disabled={isLoading}>
                {isLoading ? 'Bitte warten...' : 'Abschicken'}
              </button>
            </div>
          )}

          {currentStep > 0 && (
            <button className="button-back" onClick={handleBack}>Zurück</button>
          )}
        </>
      ) : (
        <div className="thank-you-message">
          <h2>🎉 Vielen Dank! Deine Bewerbung wurde erfolgreich gesendet.</h2>
          <p>Wir melden uns zeitnah bei Dir.</p>
        </div>
      )}
    </div>
  );
};

export default Questionnaire;